<template>
    <el-dialog v-model="dialogVisible" title="历史会话" width="620px" :close-on-click-modal="false"
        class="history-session-dialog">
        <!-- 搜索区域 -->
        <div class="search-bar">
            <el-input v-model="searchTitle" placeholder="搜索会话标题" clearable style="width: 200px" @clear="handleSearch"
                @keyup.enter="handleSearch">
                <template #prefix>
                    <el-icon>
                        <Search />
                    </el-icon>
                </template>
            </el-input>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
        </div>

        <!-- 会话列表 -->
        <div class="session-list-wrapper" v-loading="loading">
            <div v-if="sessionList.length > 0">
                <div v-for="item in sessionList" :key="item.sessionId" class="session-item"
                    :class="{ active: item.sessionId === currentSessionId }">
                    <div class="session-content" @click="handleSelect(item)">
                        <div class="session-row">
                            <span class="session-title">{{ item.title || '未命名会话' }}</span>
                            <span class="session-status" :class="item.status === 1 ? 'status-active' : 'status-new'">
                                {{ item.status === 1 ? '进行中' : '未开始' }}
                            </span>
                        </div>
                        <div class="session-row session-row-bottom">
                            <span class="session-time">
                                <el-icon>
                                    <Clock />
                                </el-icon>
                                {{ formatTime(item.createTime) }}
                            </span>
                            <span class="session-creator" v-if="item.createName">
                                <el-icon>
                                    <User />
                                </el-icon>
                                {{ item.createName }}
                            </span>
                        </div>
                    </div>
                    <el-button type="danger" text :icon="Delete" class="delete-btn" @click.stop="handleDelete(item)">
                        删除
                    </el-button>
                </div>
            </div>

            <!-- 空状态 -->
            <el-empty v-else-if="!loading" description="暂无会话记录" />
        </div>

        <!-- 分页 -->
        <div class="pagination-wrap" v-if="total > 0">
            <el-pagination v-model:current-page="pageNum" v-model:page-size="pageSize" :total="total"
                :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next" @size-change="handleSizeChange"
                @current-change="handlePageChange" />
        </div>
    </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue';
import { ElMessageBox, ElMessage } from 'element-plus';
import { Search, Delete, Clock, User } from '@element-plus/icons-vue';
import { paginationSessionByUser, deleteSession } from '@/api/ai/chat';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    currentSessionId: {
        type: String,
        default: ''
    },
    userId: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['update:modelValue', 'select', 'delete']);

const dialogVisible = ref(false);
const loading = ref(false);
const sessionList = ref([]);
const searchTitle = ref('');
const pageNum = ref(1);
const pageSize = ref(10);
const total = ref(0);

watch(() => props.modelValue, (val) => {
    dialogVisible.value = val;
    if (val) {
        pageNum.value = 1;
        searchTitle.value = '';
        fetchList();
    }
});

watch(dialogVisible, (val) => {
    emit('update:modelValue', val);
});

async function fetchList() {
    loading.value = true;
    try {
        const res = await paginationSessionByUser({
            userId: props.userId,
            title: searchTitle.value || null,
            pageNum: pageNum.value,
            pageSize: pageSize.value
        });
        if (res?.code === 200) {
            sessionList.value = res?.rows || [];
            total.value = res.data?.total || 0;
        }
    } catch (error) {
        console.error('加载历史会话失败:', error);
    } finally {
        loading.value = false;
    }
}

function handleSearch() {
    pageNum.value = 1;
    fetchList();
}

function handleSizeChange() {
    pageNum.value = 1;
    fetchList();
}

function handlePageChange() {
    fetchList();
}

function handleSelect(item) {
    emit('select', item);
    dialogVisible.value = false;
}

async function handleDelete(item) {
    try {
        await ElMessageBox.confirm(
            `确定要删除会话"${item.title || '未命名会话'}"吗？该操作会同时删除会话下的所有消息记录，且不可恢复。`,
            '删除确认',
            {
                confirmButtonText: '确定删除',
                cancelButtonText: '取消',
                type: 'warning'
            }
        );
        const res = await deleteSession(item.sessionId);
        if (res?.code === 200) {
            ElMessage.success('删除成功');
            emit('delete', item.sessionId);
            if (item.sessionId === props.currentSessionId) {
                emit('select', null);
            }
            fetchList();
        } else {
            ElMessage.error(res?.msg || '删除失败');
        }
    } catch (error) {
        if (error !== 'cancel') {
            console.error('删除会话失败:', error);
        }
    }
}

function formatTime(time) {
    if (!time) return '未知时间';
    const date = new Date(time);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}-${day} ${hours}:${minutes}`;
}
</script>

<style lang="scss" scoped>
.search-bar {
    display: flex;
    gap: 10px;
    margin-bottom: 12px;
}

.session-list-wrapper {
    height: 420px;
    overflow-y: auto;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
}

.session-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: #f5f7fa;

        .delete-btn {
            opacity: 1;
        }
    }

    &.active {
        background-color: #e6f7ff;

        .session-title {
            color: #1890ff;
        }
    }
}

.session-content {
    flex: 1;
    cursor: pointer;
    overflow: hidden;
}

.session-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    &.session-row-bottom {
        margin-bottom: 0;
        margin-top: 4px;
    }
}

.session-title {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
}

.session-status {
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 4px;
    flex-shrink: 0;

    &.status-active {
        color: #67c23a;
        background: #f0f9eb;
    }

    &.status-new {
        color: #909399;
        background: #f4f4f5;
    }
}

.session-time,
.session-creator {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    color: #999;
}

.delete-btn {
    opacity: 0;
    transition: opacity 0.2s;
    flex-shrink: 0;
    margin-left: 8px;
}

.pagination-wrap {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
}
</style>
